import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Play,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Share2,
  Download,
  Eye,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia } from "@/lib/api";
import { getPlayerSource } from "@/lib/playerSource";
import { buildShareUrl } from "@/lib/shareUrl";
import { useAdminMode } from "@/lib/adminState";
import { AD_CONFIG } from "@/lib/ads";
import NativeBannerAd from "@/components/ads/NativeBannerAd";
import VideoCard from "@/components/VideoCard";

const CATEGORY_LABEL = {
  "leaked-sextape": "Leaked Sextape",
  "sleeping-sex": "Sleeping Sex",
  "animation-xvideos": "Animation xvideos",
  "cctv-hidden": "CCTV Hidden",
  "african-leaked": "African Leaked",
};

const fmtViews = (n) => {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
};

export const VideoView = () => {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [showPlayer, setShowPlayer] = useState(false);
  const [comments, setComments] = useState([]);
  const [nick, setNick] = useState("");
  const [msg, setMsg] = useState("");
  const viewedRef = useRef(false);
  const playerRef = useRef(null);
  const adminMode = useAdminMode();

  const reload = () => {
    api.get(`/videos/${id}`).then((r) => setVideo(r.data));
  };

  useEffect(() => {
    setShowPlayer(false);
    viewedRef.current = false;
    reload();
    api.get(`/videos/${id}/similar`).then((r) => setSimilar(r.data));
    api.get(`/videos/${id}/comments`).then((r) => setComments(r.data));
    // Scroll the user back to the top so the new title + player are visible
    // when they tap a "Similar videos" card from the bottom of the page.
    window.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePlay = async () => {
    // Fire both sponsored direct links on play — within the user gesture so
    // the browser allows window.open. The video continues to play in this tab.
    if (!adminMode) {
      try {
        AD_CONFIG.playDirectLinks.forEach((url) => {
          window.open(url, "_blank", "noopener,noreferrer");
        });
      } catch (e) {}
    }
    setShowPlayer(true);
    if (!viewedRef.current) {
      viewedRef.current = true;
      try {
        const res = await api.post(`/videos/${id}/view`);
        setVideo((v) => (v ? { ...v, views: res.data.views } : v));
      } catch (e) {}
    }
    setTimeout(() => {
      if (playerRef.current) playerRef.current.play().catch(() => {});
    }, 100);
  };

  const handleLike = async () => {
    const r = await api.post(`/videos/${id}/like`);
    setVideo((v) => (v ? { ...v, likes: r.data.likes, dislikes: r.data.dislikes } : v));
    toast.success("Liked");
  };
  const handleDislike = async () => {
    const r = await api.post(`/videos/${id}/dislike`);
    setVideo((v) => (v ? { ...v, likes: r.data.likes, dislikes: r.data.dislikes } : v));
    toast("Disliked");
  };
  const handleShare = async () => {
    const shareUrl = buildShareUrl(video);
    try {
      if (navigator.share) {
        await navigator.share({ title: video?.title || "XEROTIC", url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied");
      }
    } catch (e) {}
  };
  const handleDownload = () => {
    if (!video) return;
    const url = resolveMedia(video.video_url);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${video.title.replace(/\s+/g, "-").toLowerCase()}.mp4`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const focusComments = () => {
    const el = document.getElementById("comments");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    const r = await api.post(`/videos/${id}/comments`, {
      nickname: nick || "Anonymous",
      text: msg,
    });
    setComments((c) => [r.data, ...c]);
    setMsg("");
    toast.success("Comment posted");
  };

  if (!video) {
    return (
      <div className="mx-auto max-w-6xl px-4 pt-10 md:px-12 md:pt-16">
        <div className="aspect-video w-full skeleton rounded-sm" />
      </div>
    );
  }

  const playerSource = getPlayerSource(resolveMedia(video.video_url));
  const canDownload = playerSource.kind === "video";

  return (
    <div className="mx-auto max-w-6xl px-4 pt-6 md:px-12 md:pt-16">
      {/* Title above the player as requested */}
      <div className="mb-4 animate-fade-up md:mb-6">
        <Link
          to={`/category/${video.category}`}
          className="text-[10px] uppercase tracking-[0.32em] text-[#e60039] hover:underline md:text-xs"
        >
          {CATEGORY_LABEL[video.category] || video.category}
        </Link>
        <h1
          data-testid="video-title"
          className="mt-2 font-display text-2xl tracking-tight text-[#f2f2f2] sm:text-3xl md:mt-3 md:text-4xl lg:text-5xl"
        >
          {video.title}
        </h1>
      </div>

      {/* Player */}
      <div className="relative aspect-video w-full overflow-hidden rounded-sm border border-[#1a1a1a] bg-black">
        {!showPlayer ? (
          <button
            data-testid="video-play-button"
            onClick={handlePlay}
            className="group absolute inset-0 flex items-center justify-center"
          >
            <img
              src={resolveMedia(video.thumbnail_url)}
              alt={video.title}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 transition-colors group-hover:bg-black/55" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full border border-white/40 bg-black/40 backdrop-blur-md transition-all group-hover:scale-105 group-hover:border-[#e60039]">
              <Play
                className="h-8 w-8 translate-x-[3px] text-white group-hover:text-[#e60039]"
                fill="currentColor"
              />
            </div>
          </button>
        ) : playerSource.kind === "iframe" ? (
          <iframe
            data-testid="video-iframe"
            src={playerSource.src}
            title={video.title}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="no-referrer"
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <video
            ref={playerRef}
            data-testid="video-player"
            src={playerSource.src}
            poster={resolveMedia(video.thumbnail_url)}
            controls
            autoPlay
            playsInline
            className="h-full w-full"
          >
            Your browser does not support video.
          </video>
        )}
      </div>

      {/* Meta + actions */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 md:mt-6 md:gap-4">
        <div
          data-testid="video-views"
          className="inline-flex items-center gap-2 text-sm text-[#a3a3a3]"
        >
          <Eye className="h-4 w-4" /> {fmtViews(video.views)} views
        </div>
        <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
          <button
            data-testid="btn-like"
            onClick={handleLike}
            aria-label="Like"
            className="inline-flex items-center gap-1.5 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 py-2 text-sm text-[#f2f2f2] transition-colors hover:border-[#e60039] hover:text-[#e60039] md:px-4"
          >
            <ThumbsUp className="h-4 w-4" /> {video.likes}
          </button>
          <button
            data-testid="btn-dislike"
            onClick={handleDislike}
            aria-label="Dislike"
            className="inline-flex items-center gap-1.5 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 py-2 text-sm text-[#f2f2f2] transition-colors hover:border-[#a3a3a3] md:px-4"
          >
            <ThumbsDown className="h-4 w-4" /> {video.dislikes}
          </button>
          <button
            data-testid="btn-comment"
            onClick={focusComments}
            aria-label="Comments"
            className="inline-flex items-center gap-1.5 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 py-2 text-sm text-[#f2f2f2] transition-colors hover:border-[#e60039] hover:text-[#e60039] md:px-4"
          >
            <MessageSquare className="h-4 w-4" /> {comments.length}
          </button>
          <button
            data-testid="btn-share"
            onClick={handleShare}
            aria-label="Share"
            className="inline-flex items-center gap-1.5 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 py-2 text-sm text-[#f2f2f2] transition-colors hover:border-[#e60039] hover:text-[#e60039] md:px-4"
          >
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">Share</span>
          </button>
          {canDownload && (
            <button
              data-testid="btn-download"
              onClick={handleDownload}
              aria-label="Download"
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#e60039] px-2.5 py-2 text-sm font-medium uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#b3002c] md:px-4"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download</span>
            </button>
          )}
        </div>
      </div>

      {/* Sponsored: native banner */}
      <NativeBannerAd />

      {/* Comments */}
      <section id="comments" className="mt-10 md:mt-14">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#666] md:text-xs">
          Discussion
        </div>
        <h2 className="mt-1 font-display text-xl tracking-tight text-[#f2f2f2] sm:text-2xl md:mt-2 md:text-3xl">
          Comments
        </h2>

        <form
          onSubmit={submitComment}
          data-testid="comment-form"
          className="mt-5 rounded-sm border border-[#1a1a1a] bg-[#0a0a0a] p-3 md:mt-6 md:p-4"
        >
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[200px_1fr_auto] md:gap-3">
            <input
              data-testid="comment-nick"
              value={nick}
              onChange={(e) => setNick(e.target.value)}
              placeholder="Nickname (Anonymous)"
              className="rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] placeholder:text-[#666] focus:border-[#e60039] focus:outline-none"
            />
            <input
              data-testid="comment-text"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Leave a comment..."
              className="rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] placeholder:text-[#666] focus:border-[#e60039] focus:outline-none"
            />
            <button
              type="submit"
              data-testid="comment-submit"
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#e60039] px-4 py-2 text-sm font-medium uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#b3002c]"
            >
              <Send className="h-3.5 w-3.5" /> Post
            </button>
          </div>
        </form>

        <ul data-testid="comments-list" className="mt-6 space-y-4">
          {comments.length === 0 && (
            <li className="rounded-sm border border-dashed border-[#1a1a1a] p-8 text-center text-sm text-[#666]">
              Be the first to comment.
            </li>
          )}
          {comments.map((c) => (
            <li
              key={c.id}
              className="rounded-sm border border-[#1a1a1a] bg-[#0a0a0a] p-4"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-[#f2f2f2]">{c.nickname}</span>
                <span className="text-xs text-[#666]">
                  {new Date(c.created_at).toLocaleString()}
                </span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[#a3a3a3]">{c.text}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Similar */}
      <section className="mt-14 md:mt-20">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#666] md:text-xs">
          Up Next
        </div>
        <h2 className="mt-1 font-display text-xl tracking-tight text-[#f2f2f2] sm:text-2xl md:mt-2 md:text-3xl">
          Similar videos
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3 md:mt-8">
          {similar.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
          {similar.length === 0 && (
            <div className="col-span-full rounded-sm border border-dashed border-[#1a1a1a] p-8 text-center text-sm text-[#666]">
              No similar videos yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VideoView;
